import { ChevronRight } from 'lucide-react'
import { Link, Navigate, useSearchParams } from 'react-router'
import useSWR from 'swr'

import { Loading } from '@/components/Loading'
import { InvoicingPreview } from '@/containers/Settings/InvoicingPreview/InvoicingPreview'
import { SettingsPage } from '@/containers/Settings/SettingsLayout'
import { apiFetcher } from '@/data/fetcher'
import { Settings } from '@/interfaces/Settings'
import { useAuth } from '@/lib/auth'
import { hasRole } from '@/lib/hasRole'
import { components } from '@invo.space/shared'
import { Currency } from '@/types/Currency'
import { Role } from '@/types/User'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

type DocumentSequence = components['schemas']['DocumentSequence'] & {
  initialSequence?: string
}

export default function SettingsInvoicingPage() {
  const [searchParams] = useSearchParams()
  const refSource = searchParams.get('ref')

  const { user, isLoading: isAuthLoading } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'SettingsInvoicing_getServerSideProps',
  })

  const {
    data: settings,
    isLoading: isSettingsLoading,
    mutate: mutateSettings,
  } = useSWR<Settings>('/api/v2/settings', apiFetcher)

  const {
    data: bankAccounts,
    isLoading: isBankAccountsLoading,
    mutate: mutateBankAccounts,
  } = useSWR<{ data?: InvoicingBankAccount[] }>(
    '/api/v2/bank-accounts',
    apiFetcher,
  )

  const { data: currencies, isLoading: isCurrenciesLoading } = useSWR<{
    data?: Currency[]
  }>('/api/v2/currencies', apiFetcher)

  const {
    data: invoiceSequences,
    isLoading: isSequencesLoading,
    mutate: mutateInvoiceSequences,
  } = useSWR<{ data?: DocumentSequence[] }>(
    '/api/v2/document-sequences?type=invoice',
    apiFetcher,
  )

  if (isAuthLoading) {
    return <Loading />
  }

  if (!user || !hasRole(user.roles, [Role.owner, Role.manager])) {
    return <Navigate to="/" replace />
  }

  const isLoading =
    isSettingsLoading ||
    isBankAccountsLoading ||
    isCurrenciesLoading ||
    isSequencesLoading

  const defaultInvoiceSequence =
    invoiceSequences?.data?.find((s) => s.default) ??
    invoiceSequences?.data?.[0] ??
    null

  const backHref = refSource === 'invoice' ? '/invoice/new' : '/settings'

  return (
    <SettingsPage
      title="Vzhled faktury"
      description="Takhle uvidí fakturu vaši odběratelé. Klikněte na zvýrazněnou část a upravte ji."
      breadcrumbHref="/settings/invoicing"
      width="wide"
      back={refSource === 'invoice' ? { href: backHref, label: 'Zpět na novou fakturu' } : undefined}
    >
      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_240px]">
        <InvoicingPreview
          settings={settings}
          bankAccounts={bankAccounts?.data ?? []}
          currencies={currencies?.data ?? []}
          invoiceSequence={defaultInvoiceSequence}
          isLoading={isLoading}
          onMutateSettings={() => mutateSettings()}
          onMutateBankAccounts={() => mutateBankAccounts()}
          onMutateInvoiceSequences={() => mutateInvoiceSequences()}
          refSource={refSource}
        />

        <aside className="lg:sticky lg:top-6 lg:self-start">
          <p className="mb-2 text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">
            Upravit samostatně
          </p>
          <ul className="divide-y overflow-hidden rounded-lg border text-sm">
            {[
              { href: '/settings/supplier', label: 'Fakturační údaje' },
              { href: '/settings/bank-accounts', label: 'Bankovní účty' },
              { href: '/settings/currencies', label: 'Měny' },
              { href: '/settings/document-sequences', label: 'Číselné řady' },
              { href: '/settings/footnote', label: 'Text v patičce' },
              { href: '/settings/signature', label: 'Razítko nebo podpis' },
            ].map((link) => (
              <li key={link.href}>
                <Link
                  to={link.href}
                  className="flex items-center justify-between px-3 py-2.5 transition-colors hover:bg-muted"
                >
                  {link.label}
                  <ChevronRight className="size-4 text-muted-foreground" />
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      </div>
    </SettingsPage>
  )
}
